import { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { AppMark } from '../../components/ui/Icon';

import { AdminAccountMenu } from './AdminAccountMenu';
import { AdminSidebar } from './AdminSidebar';

const FOCUSABLE_IN_DRAWER =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function AdminLayout() {
  const location = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);

  // Close the drawer whenever the route changes (a nav link was tapped).
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  // Body-scroll-lock + keyboard containment while the drawer is open; restore focus on close.
  useEffect(() => {
    if (!navOpen) return;
    const prevOverflow = document.body.style.overflow;
    const focusFrame = window.requestAnimationFrame(() => {
      sidebarRef.current?.querySelector<HTMLElement>(FOCUSABLE_IN_DRAWER)?.focus();
    });
    document.body.style.overflow = 'hidden';
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        setNavOpen(false);
        return;
      }
      if (e.key !== 'Tab') return;

      const sidebar = sidebarRef.current;
      if (sidebar === null) return;
      const focusable = Array.from(sidebar.querySelectorAll<HTMLElement>(FOCUSABLE_IN_DRAWER));
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      if (!sidebar.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKeyDown);
      // Reading the ref at cleanup time is the point here, so the rule's
      // "copy toggleRef.current into a variable inside the effect" advice is
      // declined rather than applied. The toggle button is rendered
      // unconditionally in the header (CSS hides it above 768px; it is never
      // unmounted), so while this layout is mounted the node cannot differ
      // between setup and cleanup — the drawer-close case, where we do want
      // focus pulled back onto the control that opened it. The one case where
      // the two readings diverge is unmount: React has already detached the ref
      // by the time a passive cleanup runs, so `?.` correctly no-ops, whereas a
      // captured variable would call focus() on a node React just removed.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      toggleRef.current?.focus();
    };
  }, [navOpen]);

  return (
    <div className={`admin-shell${navOpen ? ' admin-nav-open' : ''}`}>
      <header className="admin-header">
        <div className="admin-header-left">
          <button
            type="button"
            ref={toggleRef}
            className="admin-nav-toggle tb-icon-btn"
            aria-label="Toggle navigation menu"
            aria-controls="admin-sidebar"
            aria-expanded={navOpen}
            onClick={() => setNavOpen((open) => !open)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <div className="admin-brand">
            <AppMark />
            <span>Neomaris admin</span>
          </div>
        </div>
        <div className="admin-header-right">
          <AdminAccountMenu />
        </div>
      </header>
      <div className="admin-body">
        <div
          className={`admin-scrim${navOpen ? ' is-open' : ''}`}
          aria-hidden="true"
          onClick={() => setNavOpen(false)}
        />
        <AdminSidebar ref={sidebarRef} />
        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
